import React from "react";

const HIGHLIGHT_DURATION_MS = 4600;
const EMPTY_KEYS = new Set();

// Retain the successful move until its destination has actually been rendered.
// Loading Soir (or refreshing Jour) must not consume the animation's duration.
export default function useSuspendedArrivalHighlight({
  dispatchDate, dispatchPeriod, deliveries, requestedStepKeys, requestedPeriod, highlightSurface,
}) {
  const [highlightedKeys, setHighlightedKeys] = React.useState(EMPTY_KEYS);
  const [pending, setPending] = React.useState(null);
  const lastRequestRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const scope = `${dispatchDate}:${dispatchPeriod}`;
  const scopeRef = React.useRef(scope);
  const availableSignature = JSON.stringify((deliveries || []).map((item) => `delivery:${item.delivery_id}`).sort());

  React.useEffect(() => {
    if (scopeRef.current !== scope) lastRequestRef.current = requestedStepKeys;
    scopeRef.current = scope;
    setPending(null);
    setHighlightedKeys(EMPTY_KEYS);
    return () => {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [dispatchDate, dispatchPeriod]);

  React.useEffect(() => {
    if (highlightSurface !== "suspended" || requestedPeriod !== dispatchPeriod
      || !(requestedStepKeys instanceof Set) || !requestedStepKeys.size
      || lastRequestRef.current === requestedStepKeys) return;
    lastRequestRef.current = requestedStepKeys;
    window.clearTimeout(timerRef.current);
    setHighlightedKeys(EMPTY_KEYS);
    setPending({ scope, keys: new Set([...requestedStepKeys].filter((key) => key.startsWith("delivery:"))) });
  }, [dispatchDate, dispatchPeriod, requestedStepKeys, requestedPeriod, highlightSurface]);

  React.useEffect(() => {
    const availableKeys = new Set(JSON.parse(availableSignature));
    const pendingKeys = pending?.scope === scope ? pending.keys : EMPTY_KEYS;
    if (!pendingKeys.size || ![...pendingKeys].every((key) => availableKeys.has(key))) return;
    setHighlightedKeys(pendingKeys);
    setPending(null);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setHighlightedKeys(EMPTY_KEYS);
    }, HIGHLIGHT_DURATION_MS);
  }, [availableSignature, pending, scope]);

  return highlightedKeys;
}
